import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { waNumber, waLink } from '../utils/whatsapp';
import {
  FiXCircle,
  FiRefreshCw,
  FiHome,
  FiAlertTriangle,
  FiPhone,
  FiMail
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import API from '../services/api';

const SUPPORT_WHATSAPP =
  process.env.REACT_APP_SUPPORT_WHATSAPP || '';
const SUPPORT_PHONE =
  process.env.REACT_APP_SUPPORT_PHONE || '';
const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || '';

// Normalisation centralisée : les formats 00212... et 06... produisaient un
// lien wa.me invalide, visible surtout sur iPhone (Safari suit l'URL telle quelle).
const buildWhatsappLink = (phone) => {
  const num = waNumber(phone);
  if (!num) return null;
  return waLink(num);
};

const PaymentFailed = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const orderId =
          localStorage.getItem('pendingOrderId') ||
          searchParams.get('orderId');

        if (orderId) {
          const response = await API.post('/payment/verify', { orderId });

          if (response?.data?.success && response.data.data) {
            setErrorDetails(response.data.data);
          }
        }

        const errorCode =
          searchParams.get('ErrCode') || searchParams.get('errorCode');
        const errorMsg =
          searchParams.get('ErrMsg') || searchParams.get('errorMsg');
        const procReturnCode = searchParams.get('ProcReturnCode');

        if (errorCode || errorMsg || procReturnCode) {
          setErrorDetails((prev) => ({
            ...(prev || {}),
            errorCode,
            errorMsg,
            procReturnCode
          }));
        }
      } catch (error) {
        console.error('❌ Erreur vérification paiement:', error);
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  const handleRetry = () => {
    const orderId = localStorage.getItem('pendingOrderId');
    if (orderId) {
      navigate('/checkout');
    } else {
      toast.error('Commande introuvable');
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-16 w-16 border-b-4 border-red-600 rounded-full mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Vérification...</p>
        </div>
      </div>
    );
  }

  const whatsappLink = buildWhatsappLink(SUPPORT_WHATSAPP);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
            <FiXCircle className="h-12 w-12 text-red-600" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Paiement échoué
          </h2>

          <p className="text-gray-600 mb-6">
            Votre paiement n&apos;a pas pu être traité.
            Veuillez réessayer ou utiliser un autre moyen de paiement.
          </p>

          {errorDetails && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left text-sm">
              <div className="flex items-start">
                <FiAlertTriangle className="text-red-600 mt-0.5 mr-2" />
                <div>
                  {errorDetails.errorMsg && (
                    <p className="text-red-700 mb-1">
                      {errorDetails.errorMsg}
                    </p>
                  )}

                  {errorDetails.errorCode && (
                    <p className="text-xs text-red-600">
                      Code erreur : {errorDetails.errorCode}
                    </p>
                  )}

                  {errorDetails.procReturnCode && (
                    <p className="text-xs text-red-600">
                      Code retour : {errorDetails.procReturnCode}
                    </p>
                  )}

                  {errorDetails.transactionId && (
                    <p className="text-xs text-gray-600 mt-1">
                      Transaction ID : {errorDetails.transactionId}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold mb-3">Causes possibles :</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Fonds insuffisants</li>
              <li>• Informations de carte incorrectes</li>
              <li>• Carte expirée ou bloquée</li>
              <li>• Limite de paiement dépassée</li>
              <li>• Problème de connexion</li>
            </ul>
          </div>

          <div className="space-y-3 mb-6">
            <button
              onClick={handleRetry}
              className="w-full flex items-center justify-center px-6 py-3 rounded-md text-white bg-primary-600 hover:bg-primary-700 transition"
            >
              <FiRefreshCw className="mr-2" />
              Réessayer le paiement
            </button>

            <button
              onClick={() => navigate('/cart')}
              className="w-full px-6 py-3 rounded-md border bg-white hover:bg-gray-50 transition"
            >
              Retour au panier
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center px-6 py-3 text-gray-600 hover:text-gray-900 transition"
            >
              <FiHome className="mr-2" />
              Accueil
            </button>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm text-gray-500 mb-4">
              Besoin d&apos;aide ? Contactez notre support :
            </p>

            <div className="flex flex-col items-center space-y-3">
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="w-64 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition"
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
              )}

              {SUPPORT_PHONE && (
                <a
                  href={`tel:${SUPPORT_PHONE}`}
                  className="w-64 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  <FiPhone />
                  {SUPPORT_PHONE}
                </a>
              )}

              {SUPPORT_EMAIL && (
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="w-64 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition"
                >
                  <FiMail />
                  {SUPPORT_EMAIL}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailed;
